import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import apiServices from '../../../../../services/apiService';
import { primaryColor } from '../../../../../utils/comon';

const getInputStyle = () => `border border-[#E2E8F0] px-3 py-2 rounded-md w-full text-sm text-[#0F172A] bg-[#F8FAFC] placeholder:text-[#94A3B8] placeholder:text-xs transition-all duration-200 focus:outline-none focus:ring-1 hover:border-gray-400 focus:ring-[${primaryColor()}]`;
const labelStyle = "text-xs mb-1 text-[#0F172A] font-semibold block";

const TenancyBasicForm = forwardRef(({ onNext, siteId, tenantUserId, userData, tenancyData, setGlobalLoading }, ref) => {
    const [tenancyId, setTenancyId] = useState(null);
    const [formData, setFormData] = useState({
        tenancyStartDate: "",
        tenancyEndDate: "",
        monthlyRentAmount: "",
        securityDeposit: ""
    });

    useEffect(() => {
        if (tenancyData) {
            setTenancyId(tenancyData.TenancyId);
            setFormData({
                tenancyStartDate: tenancyData.TenancyStartDate ? tenancyData.TenancyStartDate.split('T')[0] : "",
                tenancyEndDate: tenancyData.TenancyEndDate ? tenancyData.TenancyEndDate.split('T')[0] : "",
                monthlyRentAmount: tenancyData.MonthlyRentAmount || "",
                securityDeposit: tenancyData.SecurityDeposit || ""
            });
        }
    }, [tenancyData]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    useImperativeHandle(ref, () => ({
        submit: async () => {
            if (!formData.tenancyStartDate) {
                toast.warning("Please fill all required fields");
                return false;
            }

            setGlobalLoading(true);
            try {
                const payload = {
                    id: tenancyId || 0,
                    unitId: Number(userData?.UnitId || 0),
                    siteId: Number(siteId),
                    ownershipRecordId: Number(userData?.OwnershipId || 0),
                    tenantUserId: Number(tenantUserId),
                    tenancyStartDate: formData.tenancyStartDate ? formData.tenancyStartDate.split('T')[0] : null,
                    tenancyEndDate: formData.tenancyEndDate ? formData.tenancyEndDate.split('T')[0] : null,
                    monthlyRentAmount: Number(formData.monthlyRentAmount),
                    securityDeposit: Number(formData.securityDeposit),
                    createdBy: "System",
                    modifiedBy: "System",
                    isActiveId: 1
                };

                const response = await apiServices({
                    method: "PUT",
                    url: "/CommUnitTenancy/updateTenancyData",
                    data: payload
                });

                if (response.success || response.isSuccess || response.IsSuccess) {
                    toast.success("Tenancy details saved successfully");
                    const newTenancyId = response.data?.Id || response.data?.data?.Id || response.data?.tenancyId || 4087;
                    onNext(newTenancyId);
                    return true;
                } else {
                    toast.error(response.message || "Failed to save tenancy details");
                    return false;
                }
            } catch (error) {
                console.error("Save Tenancy Error:", error);
                toast.error("An error occurred while saving tenancy details");
                return false;
            } finally {
                setGlobalLoading(false);
            }
        }
    }));

    return (
        <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="border border-[#E2E8F0] rounded-xl overflow-hidden mb-6">
                <div className="bg-white flex items-center justify-between p-4 border-b border-[#E2E8F0] cursor-pointer hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faAddressCard} className="text-[#3B42F6]" />
                        <h3 className="font-bold text-[#0F172A] text-sm">Tenancy Details</h3>
                    </div>
                    <FontAwesomeIcon icon={faChevronDown} className="text-slate-400 text-xs" />
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white">
                    <div className="flex flex-col">
                        <label className={labelStyle}>Tenancy Start Date <span className="text-red-500">*</span></label>
                        <input
                            id="tenancyStartDate" type="date" value={formData.tenancyStartDate ? formData.tenancyStartDate.split('T')[0] : ''}
                            onChange={handleInputChange}
                            className={getInputStyle()} required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Tenancy End Date</label>
                        <input
                            id="tenancyEndDate" type="date" value={formData.tenancyEndDate ? formData.tenancyEndDate.split('T')[0] : ''}
                            onChange={handleInputChange}
                            className={getInputStyle()}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Monthly Rent Amount</label>
                        <input
                            id="monthlyRentAmount" type="number" value={formData.monthlyRentAmount} onChange={handleInputChange}
                            placeholder="Enter Monthly Rent" className={getInputStyle()}
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className={labelStyle}>Security Deposit</label>
                        <input
                            id="securityDeposit" type="number" value={formData.securityDeposit} onChange={handleInputChange}
                            placeholder="Enter Security Deposit" className={getInputStyle()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default TenancyBasicForm;
